"use client";

import React from "react";
import { Loader2, Upload, CheckCircle2, AlertCircle } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => {
  return <input className={`flex h-12 w-full rounded-md border border-white/10 bg-zinc-900/40 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-zinc-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ${className}`} ref={ref} {...props} />;
});
Input.displayName = "Input";
const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={`flex min-h-[120px] w-full rounded-md border border-white/10 bg-zinc-900/40 px-3 py-2 text-sm ring-offset-background placeholder:text-zinc-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ${className}`} ref={ref} {...props} />;
});
Textarea.displayName = "Textarea";
const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(({
  className,
  ...props
}, ref) => {
  return <div data-section-id="35689" className="relative">
        <select className={`flex h-12 w-full appearance-none rounded-md border border-white/10 bg-zinc-900/40 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ${className}`} ref={ref} {...props} />
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-zinc-500">
          <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
          </svg>
        </div>
      </div>;
});
Select.displayName = "Select";
function HackathonRegistrationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-12 text-center space-y-6 bg-secondary/10 backdrop-blur-md border border-secondary/20 rounded-2xl animate-in fade-in zoom-in duration-500">
        <div className="h-24 w-24 rounded-full bg-secondary/20 flex items-center justify-center">
          <CheckCircle2 className="h-12 w-12 text-secondary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-3xl font-serif font-black text-foreground uppercase tracking-tighter">Challenge Accepted!</h3>
          <p className="text-muted-foreground max-w-md mx-auto text-lg">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-secondary/50 hover:bg-secondary/10 rounded-full px-8 py-6 font-bold uppercase tracking-widest transition-all">
          Enroll Another Athlete
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-sans font-semibold">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jordan Smith" {...field} />
              </FormControl>
              <FormDescription>Enter your first and last name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-sans font-semibold">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="athlete@performance.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-sans font-semibold">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 800-CAMP-NOW" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="college" rules={{
        required: "Club/Team name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-sans font-semibold">Current Club / Team</FormLabel>
              <FormControl>
                <Input placeholder="Elite Performance Academy" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="year" rules={{
        required: "Athlete category is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-sans font-semibold">Athlete Category</FormLabel>
              <FormControl>
                <Select {...field}>
                  <option value="" disabled>
                    Select your level
                  </option>
                  {["U16 Academy", "U18 Academy", "U21 Academy", "Elite / Pro", "Masters Level"].map((option, i) => <option key={i} value={option} className="bg-zinc-900 text-foreground">
                      {option}
                    </option>)}
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="team_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-sans font-semibold">Position / Specialty</FormLabel>
              <FormControl>
                <Input placeholder="Point Guard / Striker" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="project_idea" rules={{
      required: "Performance goals are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-sans font-semibold">Performance Goals & Objectives</FormLabel>
            <FormControl>
              <Textarea placeholder="What do you hope to achieve during these 12 days of high-intensity training?" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="resume" render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-sans font-semibold">Medical Clearance (PDF)</FormLabel>
            <FormControl>
              <div className="flex items-center gap-4">
                <Input type="file" accept=".pdf" className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-secondary/10 file:text-secondary hover:file:bg-secondary/20 cursor-pointer" onChange={e => {
            onChange(e.target.files?.[0]);
          }} {...field} />
              </div>
            </FormControl>
            <FormDescription>PDF format, max 5 MB.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <div className="pt-4 flex justify-center">
        <Button type="submit" disabled={isSubmitting} size="lg" className="w-full md:w-auto min-w-[280px] text-lg h-16 rounded-full bg-gradient-to-r from-secondary to-orange-700 font-sans font-bold uppercase tracking-widest shadow-[0_0_20px_rgba(249,115,22,0.4)] hover:shadow-[0_0_40px_rgba(249,115,22,0.6)] hover:scale-105 transition-all duration-300">
          {isSubmitting ? <>
              <Loader2 className="animate-spin mr-2 h-5 w-5" />
              Enrolling...
            </> : "Secure Your Spot"}
        </Button>
      </div>
    </Form>;
}
export default function RegisterForTheHackathon() {
  return <section id="join-the-camp" className="dark bg-background text-foreground relative py-24 px-4 md:px-8 overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-gradient-to-r from-secondary/50 to-orange-600/30 blur-[140px] rounded-full pointer-events-none opacity-60" />

      {}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none mix-blend-overlay" />

      <div className="relative z-10 max-w-4xl mx-auto">
        <div className="text-center mb-16 space-y-4 animate-in fade-in slide-in-from-bottom-16 duration-1000 ease-out fill-mode-both">
          <h2 className="text-6xl md:text-8xl font-serif font-black tracking-tighter uppercase bg-clip-text text-transparent bg-gradient-to-b from-white to-white/70 drop-shadow-2xl">
            Train With The Best
          </h2>
          <div className="space-y-2">
            <p className="text-xl md:text-2xl font-sans font-bold tracking-wide text-secondary uppercase">
              August 17-28 | Elite Performance Camp
            </p>
            <p className="text-lg md:text-xl text-muted-foreground/80 max-w-2xl mx-auto font-sans leading-relaxed">
              Push your limits and redefine what's possible. Join the 12-day intensive program designed for serious athletes.
            </p>
          </div>
        </div>

        <div className="bg-zinc-900/40 backdrop-blur-2xl border border-white/5 rounded-3xl p-6 md:p-10 shadow-2xl">
          <FormProvider formId="camp_registration_elite" sectionName="join-the-camp" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          college: "",
          year: "",
          team_name: "",
          project_idea: "",
          resume: null
        }} validationRules={{
          full_name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          college: {
            required: true
          },
          year: {
            required: true
          },
          project_idea: {
            required: true
          }
        }} submitText="Secure Your Spot" successMessage="You've successfully secured a spot in the Elite Performance Camp! Gear list and itinerary arriving in your inbox shortly.">
            <HackathonRegistrationForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}